import { useEffect, useState } from 'react';

import { useAppSelector } from '../../../hooks';
import { selectActiveVersion } from '../../../store/selectors/workspace.selector.ts';
import { isCalculationCompleted, isJobCompleted } from '../../../utils/jobStatus.ts';
import { isFullyCatalogueAssigned } from '../../../utils/lampMarketMatch';
import { selectWorkspaceMeasurementData } from '../../../utils/measurementCalculation/measurementSource';
import { probeWebGpuSupport } from '../scene/realtime/webgpuSupport.ts';

/** Which workspace view modes the active version can show (same rules as the legacy sidebar). */
export function useViewModeAvailability() {
  const version = useAppSelector(selectActiveVersion);
  const hasModels = useAppSelector((s) => s.workspace.uploadedModels.length > 0);
  const applied = useAppSelector((s) => s.workspace.appliedLampSimilaritySelection);
  const measurementData = useAppSelector(selectWorkspaceMeasurementData);

  // Real-time render needs WebGPU; null while the probe runs.
  const [webGpuSupported, setWebGpuSupported] = useState<boolean | null>(null);
  useEffect(() => {
    let cancelled = false;
    void probeWebGpuSupport().then((r) => {
      if (!cancelled) setWebGpuSupported(r.supported);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const calcCompleted = isCalculationCompleted(version);
  const fullyAssigned = isFullyCatalogueAssigned(version);
  return {
    webGpuSupported,
    fullyAssigned,
    measurementAvailable:
      (calcCompleted && !!version?.measurementFileId) || fullyAssigned || (!!applied && !!measurementData?.length),
    renderAvailable: isJobCompleted(version?.renderBakingJob) && !!version?.bakedModelUrl,
    realTimeAvailable: hasModels && (calcCompleted || fullyAssigned) && webGpuSupported !== false,
  };
}
